import React, { useState, useContext, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Download, Check, X, Eye, Package, FileText, Shield,
  Briefcase, Award, AlertTriangle, Calendar, Upload, File, History, ChevronDown, ChevronUp
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import JSZip from 'jszip';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

// Category icon mapping
const getCategoryIcon = (categoryName) => {
  const name = categoryName.toLowerCase();
  if (name.includes('health') || name.includes('safety') || name.includes('medical')) return Shield;
  if (name.includes('training') || name.includes('education')) return Award;
  if (name.includes('driving') || name.includes('license')) return Briefcase;
  return FileText;
};

const DocumentList = ({ qualificationTypeId }) => {
  const { user, updateUserContext } = useContext(UserContext);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentStates, setDocumentStates] = useState({});
  const [documentGroups, setDocumentGroups] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileInputRefs = useRef({});

  // Fetch data function - extracted so it can be called after updates
  const fetchData = async () => {
    if (!user?.accessToken) return null;

    try {
      const categoryResponse = await axios.get(`${API_BASE_URL}/qualificationcategory/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      const categories = categoryResponse.data;

      const typeResponse = await axios.get(`${API_BASE_URL}/qualificationtype/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      const qualificationTypes = typeResponse.data;

      // Get fresh user data with updated qualifications
      const userResponse = await axios.get(`${API_BASE_URL}/users/${user.id}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      const updatedUser = userResponse.data;

      const groupedDocs = processData(categories, qualificationTypes, updatedUser?.qualifications || []);
      setDocumentGroups(groupedDocs);

      // Auto-select category, subcategory, and document if qualificationTypeId is provided
      if (qualificationTypeId) {
        Object.entries(groupedDocs).forEach(([groupName, subcategories]) => {
          subcategories.forEach((subcat) => {
            const doc = subcat.docs.find((d) => d.qualificationTypeId === qualificationTypeId);
            if (doc) {
              setSelectedCategory(groupName);
              // Set subcategory only if category has multiple subcategories
              if (subcategories.length > 1) {
                setSelectedSubcategory(subcat.subcategoryName);
              }
              setSelectedDocument(doc);
            }
          });
        });
      }

      // Return the grouped docs for use in handleUpdate
      return groupedDocs;
    } catch (error) {
      console.error('Error fetching data:', error.message);
      return null;
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, qualificationTypeId]);

  // Reset history view when selected document changes
  useEffect(() => {
    setShowHistory(false);
    setVersionHistory([]);
  }, [selectedDocument?.userQualificationId]);

  const processData = (categories, qualificationTypes, userQualifications) => {
    const groups = {};

    categories.forEach((category) => {
      const groupName = category.name;
      groups[groupName] = [];

      if (category.subcategories.length > 0) {
        category.subcategories.forEach((subcat) => {
          const subcatTypes = qualificationTypes.filter((type) => type.subcategory === subcat.id);
          if (subcatTypes.length > 0) {
            const docs = subcatTypes.map((qualType) => {
              const userQual = userQualifications.find((q) => q.qualification_type === qualType.id);
              const fieldName = qualType.name.toLowerCase().replace(/\s+/g, '_');
              return {
                name: qualType.name,
                file: userQual?.certificate_file || null,
                date: userQual?.expiry_date || null,
                certificateNumber: userQual?.certificate_number || null,
                fieldName: fieldName,
                qualificationTypeId: qualType.id,
                userQualificationId: userQual?.id || null,
              };
            });
            groups[groupName].push({
              subcategoryName: subcat.name,
              docs: docs,
            });
          }
        });
      } else {
        const categoryTypes = qualificationTypes.filter((type) => type.category === category.id);
        if (categoryTypes.length > 0) {
          const docs = categoryTypes.map((qualType) => {
            const userQual = userQualifications.find((q) => q.qualification_type === qualType.id);
            const fieldName = qualType.name.toLowerCase().replace(/\s+/g, '_');
            return {
              name: qualType.name,
              file: userQual?.certificate_file || null,
              date: userQual?.expiry_date || null,
              certificateNumber: userQual?.certificate_number || null,
              fieldName: fieldName,
              qualificationTypeId: qualType.id,
              userQualificationId: userQual?.id || null,
            };
          });
          groups[groupName].push({
            subcategoryName: 'Documents',
            docs: docs,
          });
        }
      }

      if (groups[groupName].length === 0) {
        delete groups[groupName];
      }
    });

    return groups;
  };

  const getDocumentStatus = (doc) => {
    if (!doc.date) return false;
    const currentDate = new Date();
    const docDate = new Date(doc.date);
    currentDate.setHours(0, 0, 0, 0);
    docDate.setHours(0, 0, 0, 0);
    return docDate >= currentDate;
  };

  const getCategoryStats = (subcategories) => {
    const allDocs = subcategories.flatMap((subcat) => subcat.docs);
    const completeDocs = allDocs.filter((doc) => getDocumentStatus(doc));
    const expiringDocs = allDocs.filter((doc) => {
      const status = getExpiryStatus(doc.date);
      return status === 'expiring' || status === 'expired';
    });

    return {
      total: allDocs.length,
      complete: completeDocs.length,
      expiring: expiringDocs.length,
      percentage: allDocs.length > 0 ? Math.round((completeDocs.length / allDocs.length) * 100) : 0
    };
  };

  const getCategoryStatusColor = (stats) => {
    if (stats.expiring > 0) return 'red';
    if (stats.percentage === 100) return 'green';
    if (stats.percentage >= 50) return 'yellow';
    return 'gray';
  };

  const handleFileSelection = (fieldName, file) => {
    setDocumentStates((prev) => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), selectedFile: file },
    }));
  };

  const handleDateSelection = (fieldName, date) => {
    setDocumentStates((prev) => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), selectedDate: date },
    }));
  };

  const handleCertificateNumberChange = (fieldName, certificateNumber) => {
    setDocumentStates((prev) => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), selectedCertificateNumber: certificateNumber },
    }));
  };

  const fetchVersionHistory = async (userQualificationId) => {
    if (!userQualificationId || !user?.accessToken) {
      setVersionHistory([]);
      return;
    }

    setLoadingHistory(true);
    try {
      // Fetch the specific qualification with history
      const response = await axios.get(
        `${API_BASE_URL}/userqualification/${userQualificationId}/?include_history=true`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );

      if (response.data.version_history) {
        setVersionHistory(response.data.version_history);
      } else {
        // If no version_history, this is the only version
        setVersionHistory([{
          id: response.data.id,
          version: response.data.version || 1,
          expiry_date: response.data.expiry_date,
          certificate_number: response.data.certificate_number,
          certificate_file: response.data.certificate_file,
          is_current: response.data.is_current,
          created_at: response.data.created_at,
          superseded_at: response.data.superseded_at
        }]);
      }
    } catch (error) {
      console.error('Error fetching version history:', error);
      setVersionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && selectedDocument?.userQualificationId) {
      fetchVersionHistory(selectedDocument.userQualificationId);
    }
    setShowHistory(!showHistory);
  };

  const handleUpdate = async (fieldName, qualificationTypeId, userQualificationId) => {
    const { selectedFile, selectedDate, selectedCertificateNumber } = documentStates[fieldName] || {};
    const existingDoc = Object.values(documentGroups)
      .flatMap(subcats => subcats)
      .flatMap(subcat => subcat.docs)
      .find(doc => doc.fieldName === fieldName);
    const existingFile = existingDoc?.file;
    const existingDate = existingDoc?.date;
    const existingCertificateNumber = existingDoc?.certificateNumber;

    // Check if certificate number was explicitly changed (including clearing it)
    const certificateNumberChanged = selectedCertificateNumber !== undefined &&
      selectedCertificateNumber !== existingCertificateNumber;

    if (!selectedFile && !selectedDate && !certificateNumberChanged) {
      alert('No changes detected. Please update at least one field.');
      return;
    }

    if (selectedDate && !selectedFile) {
      alert('A new certificate file is required when updating the expiry date.');
      return;
    }

    const fileToSend = selectedFile || existingFile;
    const dateToSend = selectedDate || existingDate;

    if (!dateToSend) {
      alert('Expiry date is REQUIRED. You cannot add a qualification without an expiry date.');
      return;
    }

    if (!fileToSend) {
      alert('Certificate file is required for adding or updating a qualification.');
      return;
    }

    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
      const maxSize = 5 * 1024 * 1024;
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('Invalid file type. Please upload a PDF, PNG, or JPG file.');
        return;
      }
      if (selectedFile.size > maxSize) {
        alert('File too large. Maximum size is 5 MB.');
        return;
      }
    }

    if (selectedDate) {
      const date = new Date(selectedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isNaN(date.getTime())) {
        alert('Invalid date format. Please select a valid date.');
        return;
      }
      if (date < today) {
        alert('Expiry date must be today or in the future.');
        return;
      }
    }

    if (selectedCertificateNumber && selectedCertificateNumber.trim().length > 255) {
      alert('Certificate number cannot exceed 255 characters.');
      return;
    }

    const formData = new FormData();
    formData.append('user', user.id);
    formData.append('qualification_type', qualificationTypeId);
    if (selectedFile) formData.append('certificate_file', selectedFile);
    if (selectedDate) formData.append('expiry_date', new Date(selectedDate).toISOString().split('T')[0]);
    // Always send certificate_number if it was changed (including empty string to clear it)
    if (certificateNumberChanged) {
      formData.append('certificate_number', selectedCertificateNumber || '');
    }

    try {
      // Always use POST - backend will automatically handle versioning
      // If user already has this qualification, backend creates new version and marks old as superseded
      const url = `${API_BASE_URL}/userqualification/`;

      await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Qualification updated successfully!');
      setDocumentStates((prev) => ({ ...prev, [fieldName]: {} }));
      if (fileInputRefs.current[fieldName]) {
        fileInputRefs.current[fieldName].value = null;
      }

      // Update user context and refresh the document list
      await updateUserContext();
      const refreshedGroups = await fetchData();

      // Update the selected document to show fresh data
      if (refreshedGroups && selectedDocument && selectedDocument.qualificationTypeId === qualificationTypeId) {
        // Find the updated document in the refreshed data
        const updatedDoc = Object.values(refreshedGroups)
          .flatMap(subcats => subcats)
          .flatMap(subcat => subcat.docs)
          .find(doc => doc.qualificationTypeId === qualificationTypeId);

        if (updatedDoc) {
          setSelectedDocument(updatedDoc);
        }
      }
    } catch (error) {
      console.error('Error updating qualification:', error);

      let errorMessage = 'Failed to update qualification.';

      if (error.response?.data) {
        const errorData = error.response.data;

        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const fieldErrors = [];

          if (errorData.expiry_date) {
            fieldErrors.push(`Expiry Date: ${Array.isArray(errorData.expiry_date) ? errorData.expiry_date.join(', ') : errorData.expiry_date}`);
          }
          if (errorData.certificate_file) {
            fieldErrors.push(`Certificate File: ${Array.isArray(errorData.certificate_file) ? errorData.certificate_file.join(', ') : errorData.certificate_file}`);
          }
          if (errorData.certificate_number) {
            fieldErrors.push(`Certificate Number: ${Array.isArray(errorData.certificate_number) ? errorData.certificate_number.join(', ') : errorData.certificate_number}`);
          }
          if (errorData.non_field_errors) {
            fieldErrors.push(Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors.join(', ') : errorData.non_field_errors);
          }

          if (fieldErrors.length > 0) {
            errorMessage = 'Validation errors:\n' + fieldErrors.join('\n');
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  const calculateDaysToExpiry = (expiryDate) => {
    const currentDate = new Date();
    const expiry = new Date(expiryDate);
    currentDate.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffInTime = expiry - currentDate;
    return Math.ceil(diffInTime / (1000 * 60 * 60 * 24));
  };

  const getExpiryStatus = (date) => {
    if (!date) return 'none';
    const daysToExpiry = calculateDaysToExpiry(date);
    if (daysToExpiry < 0) return 'expired';
    if (daysToExpiry <= 30) return 'expiring';
    return 'valid';
  };

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      // Extract file extension from URL
      const urlParts = fileUrl.split('.');
      const extension = urlParts[urlParts.length - 1].split('?')[0];

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const downloadAllCertificates = async () => {
    try {
      const zip = new JSZip();
      let hasFiles = false;

      for (const [groupName, subcategories] of Object.entries(documentGroups)) {
        const folderName = groupName.replace(/[^a-z0-9]/gi, '_');

        for (const subcat of subcategories) {
          const subcatFolderName = subcat.subcategoryName.replace(/[^a-z0-9]/gi, '_');

          for (const doc of subcat.docs) {
            if (doc.file) {
              hasFiles = true;
              try {
                const response = await fetch(doc.file);
                const blob = await response.blob();

                const urlParts = doc.file.split('.');
                const extension = urlParts[urlParts.length - 1].split('?')[0];

                const fileName = `${doc.name.replace(/[^a-z0-9]/gi, '_')}.${extension}`;
                const filePath = subcat.subcategoryName === 'Documents'
                  ? `${folderName}/${fileName}`
                  : `${folderName}/${subcatFolderName}/${fileName}`;

                zip.file(filePath, blob);
              } catch (error) {
                console.error(`Error downloading ${doc.name}:`, error);
              }
            }
          }
        }
      }

      if (!hasFiles) {
        alert('No certificates to download');
        return;
      }

      const content = await zip.generateAsync({ type: 'blob' });

      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${user.first_name}_${user.last_name}_Certificates.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Error downloading certificates. Please try again.');
    }
  };

  const handleCategoryClick = (categoryName) => {
    if (selectedCategory === categoryName) {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedDocument(null);
    } else {
      setSelectedCategory(categoryName);
      // Don't auto-select subcategory - let user choose
      setSelectedSubcategory(null);
      setSelectedDocument(null);
    }
  };

  const handleSubcategoryClick = (subcategoryName) => {
    setSelectedSubcategory(subcategoryName);
    setSelectedDocument(null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedDocument(null);
  };

  const handleBackToSubcategories = () => {
    setSelectedSubcategory(null);
    setSelectedDocument(null);
  };

  if (!user) return <div className="p-4 text-slate-500">Loading documents...</div>;
  if (!Object.keys(documentGroups).length) return <div className="p-4 text-slate-500">Loading qualification types...</div>;

  // Get all documents for selected category
  const selectedCategoryDocs = selectedCategory
    ? documentGroups[selectedCategory].flatMap(subcat => subcat.docs)
    : [];

  return (
    <div className="h-full flex flex-col p-4 overflow-hidden">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-3xl font-bold text-slate-900">Your Documents</h2>
          <button
            disabled
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-300 text-white text-sm rounded-lg font-bold cursor-not-allowed opacity-50"
            title="Coming soon"
          >
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Download All</span>
          </button>
        </div>

        {/* Back button navigation */}
        {selectedSubcategory && (
          <button
            onClick={handleBackToSubcategories}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Subcategories
          </button>
        )}
        {selectedCategory && !selectedSubcategory && (
          <button
            onClick={handleBackToCategories}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {!selectedCategory ? (
          /* VIEW 1: Category Grid when no category selected */
          <div className="h-full overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(documentGroups).map(([groupName, subcategories]) => {
                const stats = getCategoryStats(subcategories);
                const statusColor = getCategoryStatusColor(stats);
                const Icon = getCategoryIcon(groupName);

                return (
                  <button
                    key={groupName}
                    onClick={() => handleCategoryClick(groupName)}
                    className="relative p-6 rounded-xl border border-slate-200 bg-white hover:border-brand-300 hover:shadow-lg transition-all duration-200 text-left"
                  >
                    {/* Status indicator */}
                    <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${
                      statusColor === 'green' ? 'bg-emerald-500' :
                      statusColor === 'yellow' ? 'bg-amber-500' :
                      statusColor === 'red' ? 'bg-red-500' :
                      'bg-slate-400'
                    }`} />

                    {/* Icon */}
                    <div className="mb-4">
                      <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-brand-600" />
                      </div>
                    </div>

                    {/* Category name */}
                    <h3 className="text-lg font-bold text-slate-900 mb-2 pr-6">
                      {groupName}
                    </h3>

                    {/* Stats */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-bold text-slate-900">{stats.complete} / {stats.total}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            statusColor === 'green' ? 'bg-emerald-500' :
                            statusColor === 'yellow' ? 'bg-amber-500' :
                            statusColor === 'red' ? 'bg-red-500' :
                            'bg-slate-400'
                          }`}
                          style={{ width: `${stats.percentage}%` }}
                        />
                      </div>
                      {stats.expiring > 0 && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-2 font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          <span>{stats.expiring} expiring</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : selectedCategory && !selectedSubcategory && documentGroups[selectedCategory].length > 1 ? (
          /* VIEW 2: Subcategory Grid when category has multiple subcategories */
          <div className="h-full overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-slate-900">{selectedCategory}</h2>
              <p className="text-sm text-slate-500 mt-1">Select a subcategory</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documentGroups[selectedCategory].map((subcat) => {
                const subcatDocs = subcat.docs;
                const completeDocs = subcatDocs.filter((doc) => getDocumentStatus(doc));
                const expiringDocs = subcatDocs.filter((doc) => {
                  const status = getExpiryStatus(doc.date);
                  return status === 'expiring' || status === 'expired';
                });
                const percentage = subcatDocs.length > 0 ? Math.round((completeDocs.length / subcatDocs.length) * 100) : 0;

                let statusColor = 'gray';
                if (expiringDocs.length > 0) statusColor = 'red';
                else if (percentage === 100) statusColor = 'green';
                else if (percentage >= 50) statusColor = 'yellow';

                return (
                  <button
                    key={subcat.subcategoryName}
                    onClick={() => handleSubcategoryClick(subcat.subcategoryName)}
                    className="relative p-6 rounded-xl border border-slate-200 bg-white hover:border-brand-300 hover:shadow-lg transition-all duration-200 text-left"
                  >
                    {/* Status indicator */}
                    <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${
                      statusColor === 'green' ? 'bg-emerald-500' :
                      statusColor === 'yellow' ? 'bg-amber-500' :
                      statusColor === 'red' ? 'bg-red-500' :
                      'bg-slate-400'
                    }`} />

                    {/* Icon */}
                    <div className="mb-4">
                      <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-brand-600" />
                      </div>
                    </div>

                    {/* Subcategory name */}
                    <h3 className="text-lg font-bold text-slate-900 mb-2 pr-6">
                      {subcat.subcategoryName}
                    </h3>

                    {/* Stats */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-bold text-slate-900">{completeDocs.length} / {subcatDocs.length}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            statusColor === 'green' ? 'bg-emerald-500' :
                            statusColor === 'yellow' ? 'bg-amber-500' :
                            statusColor === 'red' ? 'bg-red-500' :
                            'bg-slate-400'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      {expiringDocs.length > 0 && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-2 font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          <span>{expiringDocs.length} expiring</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* VIEW 3: Two-column split view for documents (when subcategory selected OR category has only 1 subcategory) */
          <div className="flex gap-4">
            {/* Left Column - Document List */}
            <div className="w-5/12">
              <div className="bg-white border border-slate-200 rounded-xl shadow-lg max-h-[calc(100vh-12rem)] overflow-hidden flex flex-col">
                {/* Header with gradient */}
                <div className="p-4 bg-gradient-to-r from-brand-50 to-white border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
                        {React.createElement(getCategoryIcon(selectedCategory), { className: "h-5 w-5 text-brand-600" })}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{selectedCategory}</h3>
                        {selectedSubcategory && (
                          <p className="text-sm text-slate-500">{selectedSubcategory}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={selectedSubcategory ? handleBackToSubcategories : handleBackToCategories}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Documents in selected or only subcategory */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {(() => {
                    // Get the current subcategory - either selected or first (if only one)
                    const currentSubcat = selectedSubcategory
                      ? documentGroups[selectedCategory].find(s => s.subcategoryName === selectedSubcategory)
                      : documentGroups[selectedCategory][0];

                    if (!currentSubcat) return null;

                    return currentSubcat.docs.map((doc) => {
                      const status = getDocumentStatus(doc);
                      const expiryStatus = getExpiryStatus(doc.date);
                      const isSelected = selectedDocument?.qualificationTypeId === doc.qualificationTypeId;

                      return (
                        <button
                          key={doc.qualificationTypeId}
                          onClick={() => setSelectedDocument(doc)}
                          className={`w-full p-3 rounded-lg border transition-all text-left ${
                            isSelected
                              ? 'border-brand-500 bg-brand-50 shadow-md'
                              : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/50'
                          } ${
                            expiryStatus === 'expired' ? 'border-l-4 border-l-red-500' :
                            expiryStatus === 'expiring' ? 'border-l-4 border-l-amber-500' :
                            expiryStatus === 'valid' ? 'border-l-4 border-l-emerald-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {status ? (
                              <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            ) : (
                              <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-slate-900 truncate">{doc.name}</p>
                              {doc.date && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Expires: {new Date(doc.date).toLocaleDateString()}
                                  {expiryStatus === 'expiring' && (
                                    <span className="text-amber-600 font-bold ml-1">
                                      ({calculateDaysToExpiry(doc.date)}d)
                                    </span>
                                  )}
                                  {expiryStatus === 'expired' && (
                                    <span className="text-red-600 font-bold ml-1">(Expired)</span>
                                  )}
                                </p>
                              )}
                            </div>
                            {doc.file && <File className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>

            {/* Right Column - Document Details */}
            <div className="w-7/12 bg-white rounded-xl border border-slate-200 shadow-lg flex flex-col max-h-[calc(100vh-12rem)] overflow-hidden">
              {selectedDocument ? (
                <>
                  <div className="p-4 bg-gradient-to-r from-brand-50 to-white border-b border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center">
                      <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center mr-3">
                        <FileText className="h-4 w-4 text-brand-600" />
                      </div>
                      {selectedDocument.name}
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Certificate Number */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                        Certificate Number
                      </label>
                      <input
                        type="text"
                        value={
                          documentStates[selectedDocument.fieldName]?.selectedCertificateNumber !== undefined
                            ? documentStates[selectedDocument.fieldName].selectedCertificateNumber
                            : (selectedDocument.certificateNumber || '')
                        }
                        onChange={(e) => handleCertificateNumberChange(selectedDocument.fieldName, e.target.value)}
                        placeholder="Enter certificate number"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                      />
                    </div>

                    {/* Expiry Date */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        value={documentStates[selectedDocument.fieldName]?.selectedDate || selectedDocument.date || ''}
                        onChange={(e) => handleDateSelection(selectedDocument.fieldName, e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
                      />
                    </div>

                    {/* File Upload */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                        Certificate File
                      </label>
                      <input
                        type="file"
                        ref={(el) => (fileInputRefs.current[selectedDocument.fieldName] = el)}
                        onChange={(e) => handleFileSelection(selectedDocument.fieldName, e.target.files[0])}
                        className="hidden"
                        id={`file-${selectedDocument.fieldName}`}
                      />
                      <label
                        htmlFor={`file-${selectedDocument.fieldName}`}
                        className="flex items-center justify-center gap-2 w-full px-4 py-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-brand-400 hover:bg-brand-50 cursor-pointer transition-colors bg-slate-50"
                      >
                        <Upload className="w-5 h-5 text-slate-500" />
                        <span className="text-sm text-slate-600">
                          {documentStates[selectedDocument.fieldName]?.selectedFile?.name || 'Choose file to upload'}
                        </span>
                      </label>
                      <p className="text-xs text-slate-400 mt-1.5">
                        PDF, PNG, or JPG (max 5MB)
                      </p>
                    </div>

                    {/* Current File Preview */}
                    {selectedDocument.file && !documentStates[selectedDocument.fieldName]?.selectedFile && (
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Current File</p>
                        <p className="text-sm text-slate-700 mb-3 truncate">{selectedDocument.file.split('/').pop()}</p>
                        <div className="flex gap-2">
                          <a
                            href={selectedDocument.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 hover:bg-white hover:border-brand-300 text-slate-700 rounded-lg transition-all font-bold text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            Preview
                          </a>
                          <button
                            onClick={() => handleDownload(selectedDocument.file, selectedDocument.name)}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-all font-bold text-sm shadow-lg shadow-brand-500/20"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Version History Section */}
                    {selectedDocument.userQualificationId && (
                      <div className="border-t border-slate-200 pt-4">
                        <button
                          onClick={toggleHistory}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <History className="w-4 h-4" />
                            Version History
                          </span>
                          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        {showHistory && (
                          <div className="mt-2 text-xs text-slate-600">
                            {loadingHistory ? (
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-500 border-t-transparent"></div>
                                <span>Loading...</span>
                              </div>
                            ) : versionHistory.length === 0 ? (
                              <span className="text-slate-400">No previous versions</span>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {versionHistory.map((version) => (
                                  <span
                                    key={version.id}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg ${
                                      version.is_current ? 'bg-brand-100 text-brand-700 font-bold' : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    v{version.version}
                                    {version.certificate_number && <span>• #{version.certificate_number}</span>}
                                    {version.expiry_date && <span>• {new Date(version.expiry_date).toLocaleDateString()}</span>}
                                    {version.certificate_file && (
                                      <a href={version.certificate_file} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 ml-1">
                                        <Eye className="w-3 h-3" />
                                      </a>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Update Button */}
                    {(() => {
                      const state = documentStates[selectedDocument.fieldName] || {};
                      const certNumChanged = state.selectedCertificateNumber !== undefined &&
                        state.selectedCertificateNumber !== (selectedDocument.certificateNumber || '');
                      return (state.selectedFile || state.selectedDate || certNumChanged) && (
                        <button
                          onClick={() => handleUpdate(selectedDocument.fieldName, selectedDocument.qualificationTypeId, selectedDocument.userQualificationId)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-all font-bold shadow-lg shadow-brand-500/20"
                        >
                          Update Document
                        </button>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-10 w-10 text-slate-300" />
                    </div>
                    <p className="text-lg font-medium text-slate-500">Select a document to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;
